import { Button, DatePicker, Radio, message } from '@maxtropy/components';
import { RadioChangeEvent, Space, Spin } from 'antd';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { apiV2EnergyGetAreaEnergyStatisticsPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { EnergyStaticNoProductContext } from '../index';
import { DateType, DateTypeDisplay, getDefaultTime } from '../utils';
import dayjs, { Dayjs } from 'dayjs';

import qs from 'qs';
import TopCard from './TopCard';
import BottomChart from './BottomChart';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const RightContent = () => {
  const { areaId } = useContext(EnergyStaticNoProductContext);
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [displayValue, setDisplayValue] = useState<RangeValue>();
  const calendarErrorRef = useRef(false);
  const invalidRangeRef = useRef(false);

  // 获取列表数据
  const { data, loading } = useRequest(
    () =>
      apiV2EnergyGetAreaEnergyStatisticsPost({
        areaId,
        startTime: getDefaultTime(timeResolution, displayValue!).startTime,
        endTime: getDefaultTime(timeResolution, displayValue!).endTime,
        resolution: timeResolution,
      }),
    {
      ready: !!areaId && !!displayValue,
      refreshDeps: [areaId, displayValue],
    }
  );

  useEffect(() => {
    switch (timeResolution) {
      case DateType.DAY:
        setDisplayValue([dayjs().subtract(30, 'day'), dayjs()]);
        break;
      case DateType.WEEK:
        setDisplayValue([dayjs().subtract(8, 'week'), dayjs()]);
        break;
      case DateType.MONTH:
        setDisplayValue([dayjs().subtract(11, 'month'), dayjs()]);
        break;
      case DateType.YEAR:
        setDisplayValue([dayjs().subtract(5, 'year'), dayjs()]);
        break;
    }
    calendarErrorRef.current = false;
  }, [timeResolution]);

  /** picker 类型 */
  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) return 'month';
    if (timeResolution === DateType.WEEK) return 'week';
    if (timeResolution === DateType.YEAR) return 'year';
    return 'date';
  }, [timeResolution]);

  const onRadioChange = (e: RadioChangeEvent) => {
    setTimeResolution(e.target.value);
  };

  const disabledDate = (current: Dayjs) => {
    if (!displayValue?.[0] && !displayValue?.[1]) {
      return current > dayjs().endOf('day');
    }

    const afterToday = current > dayjs().endOf('day');

    if (!displayValue[0]) return afterToday;

    switch (timeResolution) {
      case DateType.MONTH:
        return afterToday || current.diff(displayValue[0], 'month') >= 12;
      case DateType.YEAR:
        return afterToday || current.diff(displayValue[0], 'year') >= 6;
      default:
        return afterToday;
    }
  };

  const onCalendarChange = (dates: RangeValue, _: any, info?: { range?: 'start' | 'end' }) => {
    if (info?.range !== 'end') {
      invalidRangeRef.current = false;
      return;
    }

    if (!dates?.[0] || !dates?.[1]) return;

    let invalid = false;
    let errorMsg = '';

    switch (timeResolution) {
      case DateType.DAY: {
        invalid = dates[1].diff(dates[0], 'day') >= 31;
        errorMsg = '最多选择31天';
        break;
      }
      case DateType.WEEK: {
        invalid = dates[1].diff(dates[0], 'week') >= 10;
        errorMsg = '最多选择10周';
        break;
      }
      case DateType.MONTH: {
        invalid = dates[1].diff(dates[0], 'month') >= 12;
        errorMsg = '最多选择12个月';
        break;
      }
      case DateType.YEAR: {
        invalid = dates[1].diff(dates[0], 'year') >= 6;
        errorMsg = '最多选择6年';
        break;
      }
    }

    if (invalid) {
      if (!invalidRangeRef.current) {
        message.error(errorMsg);
      }
      invalidRangeRef.current = true;
      return;
    }

    invalidRangeRef.current = false;
  };

  const onChangePicker = (dates: RangeValue) => {
    if (invalidRangeRef.current) {
      return;
    }

    setDisplayValue(dates);
  };

  const exportAction = () => {
    const params = {
      areaId,
      startTime: getDefaultTime(timeResolution, displayValue!).startTime,
      endTime: getDefaultTime(timeResolution, displayValue!).endTime,
      resolution: timeResolution,
    };
    window.open(`/api/v2/energy/downAreaEnergyStatistics` + qs.stringify(params, { addQueryPrefix: true }));
  };

  return (
    <>
      <div className={styles.time_box}>
        <Space size={8}>
          <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
            <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
            <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
            <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
            <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
          </Radio.Group>

          <DatePicker.RangePicker
            picker={picker}
            style={{ width: 250 }}
            value={displayValue}
            disabledDate={disabledDate}
            onCalendarChange={onCalendarChange}
            onChange={onChangePicker}
            allowClear={false}
          />
        </Space>
        <Button type="primary" onClick={exportAction}>
          导出数据
        </Button>
      </div>
      <Spin spinning={loading} style={{ minHeight: 400 }}>
        <TopCard data={data?.tableData} />
        <BottomChart data={data?.chartData} resolution={timeResolution} />
      </Spin>
    </>
  );
};

export default RightContent;
